<template>
  <div>
    <b-row>
      <b-col>
        <b-card
          title="Shipments in progress"
          :sub-title="lastUpdated ? `Last updated: ${timeAgoString}` : ''"
        >
          <b-card-text>
            <div class="overflow-x-scroll">
              <b-table
                :items="shipments"
                :busy="loading"
                responsive
                :fields="fields"
                :sort-by.sync="sortBy"
                :sort-desc.sync="isSortDirDesc"
                stacked="sm"
                class="mb-0"
                show-empty
              >
                <template #table-busy>
                  <div class="text-center my-2">
                    <b-spinner class="align-middle mr-1" />
                    <strong>Loading...</strong>
                  </div>
                </template>

                <template #cell(document_number)="data">
                  <div class="d-flex align-items-center">
                    <div>
                      <div class="font-weight-bolder">
                        <router-link :to="{ name: 'shipment', params: { documentNumber: data.item.document_number, shipmentId: data.item.id }}">
                          {{ data.item.document_number }}
                        </router-link>
                      </div>
                    </div>
                  </div>
                </template>

                <template
                  v-if="showLogoColumn"
                  #cell(logo)="data"
                >
                  <CompanyThumb
                    :logo="data.item.company.logo"
                    :name="data.item.company.name"
                  />
                </template>

                <template #cell(arrival)="data">
                  <AdvancedProgressBar
                    :sections="arrivedSection(data.item)"
                    :width="200"
                    :expand-index="150"
                  />
                </template>

                <template #cell(customs)="data">
                  <AdvancedProgressBar :sections="declarationSections(data.item)" />
                </template>

                <template #cell(warehouse)="data">
                  <AdvancedProgressBar
                    :sections="lastMileSection(data.item)"
                    :width="300"
                    :expand-index="150"
                  />
                </template>

                <template #cell(created_at)="data">
                  {{ data.item.created_at | formatDate }}
                </template>
              </b-table>
            </div>
            <div>
              <pagination />
            </div>
          </b-card-text>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { BCard, BRow, BCol, BCardText, BSpinner, BTable } from 'bootstrap-vue'
import Vue from 'vue'
import CompanyThumb from '@/views/components/CompanyThumb.vue'
import handleError from '@/views/components/errorHandler'
import AdvancedProgressBar from '@/views/dashboard/components/AdvancedProgressBar.vue'
import { parseFilters } from '@core/utils/filter'
import { EventBus } from '@core/utils/utils'
import Pagination from '@/layouts/components/Pagination/Pagination.vue'

export default {
  components: {
    Pagination,
    CompanyThumb,
    BCard,
    BCardText,
    BCol,
    BRow,
    BSpinner,
    BTable,
    AdvancedProgressBar,
  },
  data() {
    return {
      loading: false,
      shipments: [],
      page: 1,
      sortBy: null,
      isSortDirDesc: null,
      lastUpdated: null,
      intervalId: null,
      timeAgoString: '',
    }
  },
  computed: {
    showLogoColumn() {
      return Vue.prototype.$profile().data.user.companies.length > 1
    },
    fields() {
      return [
        ...[this.showLogoColumn ? { key: 'logo', label: 'Company', sortable: false, class: 'image-column' } : null],
        { key: 'document_number', label: 'Document number', sortable: false },
        { key: 'arrival', label: 'Arrival', sortable: false },
        { key: 'customs', label: 'Customs', sortable: false },
        { key: 'warehouse', label: 'Last mile', sortable: false },
        {
          key: 'created_at',
          label: 'Created',
          sortable: true,
          thClass: 'd-none d-sm-table-cell',
          tdClass: 'd-none d-sm-table-cell',
        },
      ]
    },
  },
  created() {
    EventBus.$on('pageChange', this.handlePageChange)
    EventBus.$on('companyListUpdated', this.loadShipmentsInProgress)
    this.loadShipmentsInProgress()
    const channel = this.$pusher.subscribe('shipment-metrics')

    channel.bind('shipment-metrics-updated', () => {
      this.loadShipmentsInProgress()
    })

    this.startUpdatingTimeAgo()
  },
  beforeDestroy() {
    clearInterval(this.intervalId)
    EventBus.$off('pageChange', this.handlePageChange)
    EventBus.$off('companyListUpdated', this.loadShipmentsInProgress)
  },
  methods: {
    startUpdatingTimeAgo() {
      this.intervalId = setInterval(this.timeAgo, 1000)
    },
    timeAgo() {
      if (!this.lastUpdated) {
        this.timeAgoString = ''
      }

      const now = new Date()
      const diffInSeconds = Math.floor((now - this.lastUpdated) / 1000)

      if (diffInSeconds < 60) {
        this.timeAgoString = `${diffInSeconds} seconds ago`
      } else {
        const diffInMinutes = Math.floor(diffInSeconds / 60)
        this.timeAgoString = `${diffInMinutes} minutes ago`
      }
    },
    loadShipmentsInProgress() {
      this.loading = true
      // Request
      this.filters = { ...this.filters, page: this.page }
      this.$http.get('/v1/current-shipments', {
        params: parseFilters(this.filters),
      })
        .then(response => {
          EventBus.$emit('updateMeta', response.data.meta)
          this.$data.shipments = response.data.data
          this.lastUpdated = new Date()
          this.timeAgo()
        })
        .catch(error => {
          handleError(error, this.$toast)
        })
        .finally(() => {
          this.loading = false
        })
    },
    arrivedSection(item) {
      return [
        { label: 'not arrived', value: item.parcels_count - item.stats.arrived.count, percentage: 100 - item.stats.arrived.percentage, variant: 'light', shipmentId: item.id, link: 'event-interface/arrived_to_facility' },
        { label: 'arrived', value: item.stats.arrived.count, percentage: item.stats.arrived.percentage, variant: 'secondary' },
      ]
    },
    lastMileSection(item) {
      return [
        { label: 'avb for ready', value: item.stats.available_ready.count, percentage: item.stats.available_ready.percentage, variant: 'light', shipmentId: item.id, link: 'event-interface/ready_for_last_mile' },
        { label: 'ready', value: item.stats.ready.count, percentage: item.stats.ready.percentage, variant: 'secondary' },
        { label: 'avb for released', value: item.stats.available_released.count, percentage: item.stats.available_released.percentage, variant: 'light', shipmentId: item.id, link: 'event-interface/released_for_last_mile' },
        { label: 'released', value: item.stats.released.count, percentage: item.stats.released.percentage, variant: 'secondary' },
      ]
    },
    declarationSections(item) {
      return [
        { label: 'errors', value: item.stats.pre_check_error.count, percentage: item.stats.pre_check_error.percentage, variant: 'danger', shipmentId: item.id },
        { label: 'not sent', value: item.stats.not_sent.count, percentage: item.stats.not_sent.percentage, variant: 'secondary', shipmentId: item.id },
        { label: 'sending', value: item.stats.in_progress.count, percentage: item.stats.in_progress.percentage, variant: 'primary', shipmentId: item.id },
        { label: 'needs attention', value: item.stats.needs_attention.count, percentage: item.stats.needs_attention.percentage, variant: 'warning', shipmentId: item.id },
        { label: 'needs documents', value: item.stats.needs_documents.count, percentage: item.stats.needs_documents.percentage, variant: 'warning', shipmentId: item.id },
        { label: 'held', value: item.stats.held.count, percentage: item.stats.held.percentage, variant: 'warning', shipmentId: item.id },
        { label: 'cleared', value: item.stats.cleared.count, percentage: item.stats.cleared.percentage, variant: 'success', shipmentId: item.id },
      ]
    },
    handlePageChange(newPage) {
      this.page = newPage
      this.loadShipmentsInProgress()
    },
  },
}
</script>
<style lang="scss" scoped>
  .shipment-progress {
    &-item {
      position: relative;

      & > span {
        position: absolute;
        display: block;
        height: 100%;
        top: 0;
        left: 0;
        background: rgba(0,0,0,.1);
      }

      &:first-child, &:first-child > span {
        border-radius: 5px 0 0 5px;
      }

      &:last-child, &:last-child > span {
        border-radius: 0 5px 5px 0;
      }

      &--arrived {
        background: rgb(238,238,238);
      }

      &--cleared {
        background: rgb(168,255,216);
      }

      &--released {
        background: rgb(222,236,255);
      }
    }
  }

  .progress > span {
    line-height: 2rem;
    padding-left: 5px;
  }
</style>
